import { Link } from 'react-router-dom';
import { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import {
  Package,
  AlertTriangle,
  Calendar,
  TrendingUp,
  Scissors,
  Plus,
  ClipboardCheck,
  GitBranch,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useDashboardSummary, useAssetStatusDistribution, useRecentDeployments } from '@/hooks/use-dashboard';
import { usePermissions } from '@/hooks/use-permissions';
import { CriticalAlerts } from '@/components/dashboard/critical-alerts';

class AlertsBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('AlertsBoundary caught:', error, info);
  }
  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

function StatsCardSkeleton() {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-4 rounded" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-8 w-16 mb-1" />
        <Skeleton className="h-3 w-32" />
      </CardContent>
    </Card>
  );
}

function ChartSkeleton() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-64 mt-1" />
      </CardHeader>
      <CardContent>
        <div className="h-[300px] flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </CardContent>
    </Card>
  );
}

export function DashboardPage() {
  const { data: summary, isLoading: summaryLoading, isError: summaryError } = useDashboardSummary();
  const { data: statusDistribution, isLoading: chartLoading } = useAssetStatusDistribution();
  const { data: recentDeployments, isLoading: deploymentsLoading } = useRecentDeployments(5);
  const { canManageGear, canRecordInspections } = usePermissions();

  const totalAssets = summary?.total_assets ?? 0;
  const deployedCount = summary?.deployed_count ?? 0;
  const deployedPercentage = totalAssets > 0 ? Math.round((deployedCount / totalAssets) * 100) : 0;
  const overdueInspections = summary?.overdue_inspections ?? 0;
  const inspectionsDueThisWeek = summary?.inspections_due_this_week ?? 0;
  const ropeCuts30Days = summary?.rope_cuts_30_days ?? 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1><img src="/logos/h3gear.svg" alt="H3Gear" className="h-7" /></h1>
          <p className="text-gray-600 mt-1">Welcome back! Here's your equipment overview.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          {canManageGear && (
            <Button asChild>
              <Link to="/deployments/check-out">
                <Plus className="h-4 w-4 mr-2" />
                Check Out Asset
              </Link>
            </Button>
          )}
          {canRecordInspections && (
            <Button asChild variant="outline">
              <Link to="/inspections/new">
                <ClipboardCheck className="h-4 w-4 mr-2" />
                New Inspection
              </Link>
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryLoading ? (
          <>
            <StatsCardSkeleton />
            <StatsCardSkeleton />
            <StatsCardSkeleton />
            <StatsCardSkeleton />
          </>
        ) : summaryError ? (
          <Card className="col-span-full">
            <CardContent className="py-8 text-center">
              <AlertTriangle className="h-8 w-8 text-orange-500 mx-auto mb-2" />
              <p className="text-gray-600">Unable to load dashboard statistics</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Assets</CardTitle>
                <Package className="h-4 w-4 text-gray-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{totalAssets}</div>
                <p className="text-xs text-gray-600 mt-1">
                  {deployedPercentage}% deployed
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Overdue Inspections</CardTitle>
                <AlertTriangle className="h-4 w-4 text-orange-600" />
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${overdueInspections > 0 ? 'text-orange-600' : ''}`}>
                  {overdueInspections}
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  {overdueInspections > 0 ? 'Require immediate attention' : 'All inspections up to date'}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Due This Week</CardTitle>
                <Calendar className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{inspectionsDueThisWeek}</div>
                <p className="text-xs text-gray-600 mt-1">
                  Inspections scheduled
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Rope Cuts (30d)</CardTitle>
                <Scissors className="h-4 w-4 text-gray-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{ropeCuts30Days}</div>
                <p className="text-xs text-gray-600 mt-1">
                  Total cuts this month
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {chartLoading ? (
          <ChartSkeleton />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Asset Status Distribution</CardTitle>
              <CardDescription>Current status of all equipment</CardDescription>
            </CardHeader>
            <CardContent>
              {statusDistribution && statusDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={statusDistribution}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-gray-500">
                  No asset data available
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>Latest deployments</CardDescription>
          </CardHeader>
          <CardContent>
            {deploymentsLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-start gap-4">
                    <Skeleton className="h-9 w-9 rounded-lg" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-48" />
                    </div>
                    <Skeleton className="h-5 w-16 rounded-full" />
                  </div>
                ))}
              </div>
            ) : recentDeployments && recentDeployments.length > 0 ? (
              <div className="space-y-4">
                {recentDeployments.map((deployment) => (
                  <Link
                    key={deployment.id}
                    to={`/assets/${deployment.asset}`}
                    className="flex items-start gap-4 p-2 -mx-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="p-2 bg-green-50 rounded-lg">
                      <GitBranch className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="flex-1 space-y-1">
                      <p className="text-sm font-medium">
                        {deployment.asset_name || `Asset #${deployment.asset}`}
                        {deployment.asset_serial_number ? ` (${deployment.asset_serial_number})` : ''}
                      </p>
                      <p className="text-xs text-gray-600">
                        {deployment.action === 'check_out' ? 'Checked out' :
                         deployment.action === 'check_in' ? 'Checked in' : 'Transferred'}
                        {deployment.location_name
                          ? ` to ${deployment.location_name}`
                          : deployment.location
                            ? ` to location #${deployment.location}`
                            : ''}
                        {deployment.assigned_to_user_name
                          ? ` — ${deployment.assigned_to_user_name}`
                          : ''}
                      </p>
                    </div>
                    <Badge variant="outline" className="capitalize">
                      {deployment.approval_status}
                    </Badge>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-gray-500">
                No recent deployments
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Button asChild variant="outline" className="h-auto flex-col p-4">
              <Link to="/gear/compliance">
                <AlertTriangle className="h-6 w-6 mb-2 text-orange-600" />
                <span className="text-sm">View Compliance</span>
              </Link>
            </Button>
            {canManageGear && (
              <Button asChild variant="outline" className="h-auto flex-col p-4">
                <Link to="/assets/new">
                  <Plus className="h-6 w-6 mb-2" />
                  <span className="text-sm">Add Asset</span>
                </Link>
              </Button>
            )}
            <Button asChild variant="outline" className="h-auto flex-col p-4">
              <Link to="/reports/utilization">
                <TrendingUp className="h-6 w-6 mb-2" />
                <span className="text-sm">Utilization Report</span>
              </Link>
            </Button>
            <Button asChild variant="outline" className="h-auto flex-col p-4">
              <Link to="/locations">
                <Package className="h-6 w-6 mb-2" />
                <span className="text-sm">Manage Locations</span>
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      <AlertsBoundary>
        <CriticalAlerts />
      </AlertsBoundary>
    </div>
  );
}
